import { Injectable } from '@angular/core'
import { ApiService } from '@app/core/graphql/services/api.service'
import { ServicesSubscriberEvent } from '@app/models/others.model'
import { CreatePassInput, UpdatePassInput } from '@app/models/pass.model'
import { DuplicateActivePassesReportData } from '@app/models/log_event.model'
import {
  CHECK_ACTIVE_PASS_FOR_MOBILE,
  GET_DUPLICATE_ACTIVE_PASSES_REPORT,
  GET_OWNED_ASSET_PASSES,
  GET_PASSES,
  GET_PASSES_BY_EVENT_AND_ORDER,
  GET_PASS_WITH_CONNECTIONS,
  GET_UNREGISTERED_EVENT_PASSES,
  SET_PASS,
  SET_PASSES,
  UPDATE_PASS,
  UPDATE_SUB_EVENT_PASSES,
} from '@app/query/pass.query'
import { serializeAs } from '@app/utils/serialize'
import { pick } from 'lodash-es'
import { MessageService } from 'primeng/api'
import { catchError, map, Observable, of, Subject, tap } from 'rxjs'
import { TranslateMessagesService } from './translate-messages.service'

interface PassResult {
  name?: string
  first_name?: string
  last_name?: string
  [key: string]: unknown
}

interface PassMutationResponse {
  success: boolean
  data?: PassResult
  message?: string
  errorText?: string
}

interface PassPagination {
  totalCount: number
  totalPages: number
  currentPage: number
  hasNextPage: boolean
  hasPreviousPage: boolean
  itemsPerPage: number
}

/** One driver/operator link hanging off a vehicle or HME pass. */
export interface PassConnection {
  id: number
  connection_type: string
  metadata: string | null
  connected_pass: {
    id: number
    first_name: string | null
    last_name: string | null
    mobile: string | null
    country_code: string | null
  } | null
}

/** A vehicle/HME pass a person owns: cancelled with them and moved to their dates by PAPI. */
export interface OwnedAssetPass {
  id: number
  category: number
  /** The plate or equipment number. */
  id_number: string | null
}

/** The line the portal adds before cancelling or re-dating a person who owns vehicle/HME passes. */
export function ownedAssetWarning(translate: { instant(key: string, params?: object): string }, kind: 'cancel' | 'dates', assets: OwnedAssetPass[]): string {
  if (!assets?.length) return ''
  const plates = assets.map((a) => a.id_number || `#${a.id}`).join(', ')
  return translate.instant(kind === 'cancel' ? 'SYSTEM.LABELS.OWNED_ASSETS_CANCEL' : 'SYSTEM.LABELS.OWNED_ASSETS_DATES', { plates })
}

/** `range_allowed` out of a `time_limitations` value (JSON string or object), or null. */
export function rangeOf(timeLimitations: unknown): string | null {
  if (!timeLimitations) return null
  try {
    const parsed: unknown = typeof timeLimitations === 'string' ? JSON.parse(timeLimitations) : timeLimitations
    return typeof (parsed as { range_allowed?: unknown })?.range_allowed === 'string' ? (parsed as { range_allowed: string }).range_allowed : null
  } catch {
    return null
  }
}

/** A whole pass plus its connections — the model shape the pass/vehicle/HME modals open on. */
export type PassWithConnections = UpdatePassInput & {
  type_specific_data?: string | null
  connections: PassConnection[]
}

interface PassWithConnectionsResponse {
  success: boolean
  message?: string
  errorText?: string
  data?: {
    primary_pass?: UpdatePassInput & { type_specific_data?: string | null }
    connections?: PassConnection[]
    total_connections?: number
  }
}

interface PassListResponse {
  success: boolean
  data?: UpdatePassInput[]
  pagination?: PassPagination
}

interface ActivePassCheck {
  exists: boolean
  passes: unknown[]
}

@Injectable({
  providedIn: 'root',
})
export class PassService {
  subscriber: Subject<ServicesSubscriberEvent> = new Subject<ServicesSubscriberEvent>()
  id: string = 'PASS'
  sysMessage: ReturnType<TranslateMessagesService['create']>

  constructor(
    private API: ApiService,
    private translateMessagesService: TranslateMessagesService,
    private messageService: MessageService,
  ) {
    this.sysMessage = this.translateMessagesService.create(this.id)
  }

  createPass(payload: Partial<CreatePassInput>): Observable<PassMutationResponse> {
    return this.API.privateMutation<PassMutationResponse>(SET_PASS, (data: Record<string, unknown>) => {
      return {
        data: serializeAs(CreatePassInput, pick(Object.assign(payload, data), Object.keys(new CreatePassInput()))) as CreatePassInput,
      }
    }).pipe(
      tap((data) => {
        if (data.success) {
          this.messageService.add({
            severity: 'success',
            icon: 'pi pi-check-circle color-success',
            ...this.sysMessage.message('CREATE_SUCCESS', ''),
          })
          this.subscriber.next({
            type: `SET_${this.id}`,
            data: data.data,
          })
        }
      }),
    )
  }

  createMultiplePasses(payloads: Partial<CreatePassInput>[]): Observable<PassMutationResponse> {
    return this.API.privateMutation<PassMutationResponse>(SET_PASSES, () => {
      return {
        data: payloads,
      }
    }).pipe(
      tap((data) => {
        if (data.success) {
          this.messageService.add({
            severity: 'success',
            icon: 'pi pi-check-circle color-success',
            ...this.sysMessage.message('CREATE_SUCCESS', ''),
          })
          this.subscriber.next({
            type: `SET_${this.id}`,
            data: data.data,
          })
        }
      }),
    )
  }

  updatePass(payload: Partial<UpdatePassInput>): Observable<PassMutationResponse> {
    return this.API.privateMutation<PassMutationResponse>(UPDATE_PASS, (data: Record<string, unknown>) => {
      const obj = pick(Object.assign(payload, data), Object.keys(new UpdatePassInput()))
      return {
        data: serializeAs(UpdatePassInput, obj) as UpdatePassInput,
      }
    }).pipe(
      tap((data) => {
        if (data.success) {
          // Only set name if both first_name and last_name are provided in payload
          if (payload.first_name && payload.last_name) {
            data.data!.name = `${payload.first_name} ${payload.last_name}`
          } else if (data.data!.first_name && data.data!.last_name) {
            // Fall back to server response data if available
            data.data!.name = `${data.data!.first_name} ${data.data!.last_name}`
          } else {
            // For operations without name data (like activate/deactivate), use generic success message
            this.messageService.add({
              severity: 'success',
              icon: 'pi pi-check-circle color-success',
              ...this.sysMessage.message('UPDATE_SUCCESS_NO_ENTITY_NAME', {}),
            })
            this.subscriber.next({
              type: `UPDATE_${this.id}`,
              data: data.data,
            })
            return
          }

          this.messageService.add({
            severity: 'success',
            icon: 'pi pi-check-circle color-success',
            ...this.sysMessage.message('UPDATE_SUCCESS', data.data),
          })
          this.subscriber.next({
            type: `UPDATE_${this.id}`,
            data: data.data,
          })
        }
      }),
    )
  }

  updateSubEventPass(payload: Partial<UpdatePassInput>): Observable<PassMutationResponse> {
    return this.API.privateMutation<PassMutationResponse>(UPDATE_SUB_EVENT_PASSES, (data: Record<string, unknown>) => {
      const obj = pick(Object.assign(payload, data), Object.keys(new UpdatePassInput()))
      return {
        data: serializeAs(UpdatePassInput, obj) as UpdatePassInput,
      }
    }).pipe(
      tap((data) => {
        if (data.success) {
          this.messageService.add({
            severity: 'success',
            icon: 'pi pi-check-circle color-success',
            ...this.sysMessage.message('UPDATE_SUCCESS', data.data),
          })
          this.subscriber.next({
            type: `UPDATE_${this.id}`,
            data: data.data,
          })
        }
      }),
    )
  }

  getFilteredPasses(_data: Record<string, unknown>, pageOptions: Record<string, unknown> = {}) {
    return this.API.privateQuery<PassListResponse>(GET_PASSES, (data: Record<string, unknown>) => {
      Object.assign(data, _data)
      data['include_requirements_details'] = true
      return {
        data,
        pageOptions,
      }
    }).pipe(
      map((response) => {
        if (response.success) {
          // Return full response with pagination metadata if available
          if (response.pagination) {
            return {
              data: response.data as UpdatePassInput[],
              totalRecords: response.pagination.totalCount,
              totalPages: response.pagination.totalPages,
              currentPage: response.pagination.currentPage,
              hasNextPage: response.pagination.hasNextPage,
              hasPreviousPage: response.pagination.hasPreviousPage,
              itemsPerPage: response.pagination.itemsPerPage,
            }
          } else {
            // Fallback for old format - just return the data array
            return response.data as UpdatePassInput[]
          }
        } else {
          return [] as UpdatePassInput[]
        }
      }),
      tap((response) => {
        // Extract data array for the subscriber regardless of response format
        const passes = Array.isArray(response) ? response : response.data || []
        this.subscriber.next({
          type: `GET_${this.id}S`,
          data: passes,
        })
      }),
    )
  }

  /**
   * Hydrate ONE complete pass by id — for surfaces that hold a pass id but not a pass (the entry
   * monitor's cards). Returns the `UpdatePassInput`-shaped model the pass/vehicle/HME modals expect,
   * plus any driver/operator connections, or null when the id does not resolve for this client.
   *
   * Works for every pass category: `get_pass_with_connections` is category-agnostic, and it is the
   * only id-addressed whole-pass query available to the portal (`get_passes` has no id filter).
   */
  /**
   * The vehicle/HME passes this person owns — PAPI cancels them with the person and moves them to
   * the person's new dates (QA 15/10/2026). Empty on any failure: the warning is a courtesy, never
   * a reason to block the save.
   */
  getOwnedAssetPasses(passId: number): Observable<OwnedAssetPass[]> {
    return this.API.privateQuery<{ success: boolean; data?: OwnedAssetPass[] }>(GET_OWNED_ASSET_PASSES, () => ({ pass_id: +passId })).pipe(
      map((response) => (response?.success && Array.isArray(response.data) ? response.data : [])),
      catchError(() => of([] as OwnedAssetPass[])),
    )
  }

  getPassWithConnections(passId: number): Observable<PassWithConnections | null> {
    return this.API.privateQuery<PassWithConnectionsResponse>(GET_PASS_WITH_CONNECTIONS, () => ({ passId })).pipe(
      map((response) => {
        if (!response?.success || !response.data?.primary_pass) return null
        return {
          ...response.data.primary_pass,
          connections: response.data.connections ?? [],
        } as PassWithConnections
      }),
    )
  }

  getUnregisteredEventPasses(event_id: number, sub_event_id?: number | null) {
    return this.API.privateQuery<PassListResponse>(GET_UNREGISTERED_EVENT_PASSES, () => {
      return {
        event_id: event_id,
        sub_event_id: sub_event_id,
      }
    }).pipe(
      map((response) => {
        if (response.success) {
          return response.data as UpdatePassInput[]
        } else {
          return [] as UpdatePassInput[]
        }
      }),
      tap((passes) => {
        this.subscriber.next({
          type: `GET_${this.id}S`,
          data: passes,
        })
      }),
    )
  }

  getPassesByEventAndOrder(event_id: number, order_number: string) {
    return this.API.privateQuery<PassListResponse>(GET_PASSES_BY_EVENT_AND_ORDER, () => {
      return {
        event_id: event_id,
        order_number: order_number,
      }
    }).pipe(
      map((response) => {
        if (response.success) {
          return response.data as UpdatePassInput[]
        } else {
          return [] as UpdatePassInput[]
        }
      }),
      tap((passes) => {
        this.subscriber.next({
          type: `GET_${this.id}S`,
          data: passes,
        })
      }),
    )
  }

  checkActivePassForMobile(mobile: string, countryCode: string, eventId: number, category?: string) {
    return this.API.privateQuery<{ data?: ActivePassCheck }>(CHECK_ACTIVE_PASS_FOR_MOBILE, () => ({
      input: { mobile, country_code: countryCode || undefined, event_id: eventId, category: category || undefined }
    })).pipe(
      map((response) => (response as { data?: ActivePassCheck }).data ?? { exists: false, passes: [] })
    )
  }

  getDuplicateActivePassesReport(eventId: number, category?: string, page: number = 1, take: number = 25) {
    return this.API.privateQuery<{ data?: DuplicateActivePassesReportData }>(GET_DUPLICATE_ACTIVE_PASSES_REPORT, () => ({
      input: { event_id: eventId, category: category || undefined, page, take }
    })).pipe(
      map((response) => (response as { data?: DuplicateActivePassesReportData }).data ?? { groups: [], total_groups: 0, page: 1, total_pages: 0 })
    )
  }
}
